import React, {useEffect, useState} from "react";
import {del, get} from "../../api/api";
import Encounter from "./Encounter";
import {
    Button,
    CircularProgress,
    Container,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {EncounterModel} from "../../models/EncounterModel";
import {showMessage} from "../../utils/snackbar";


interface EncountersListProps {
    sidebarOpen: boolean
}

const EncountersList: React.FC<EncountersListProps> = ({sidebarOpen}: EncountersListProps) => {
    const [encounters, setEncounters] = useState<EncounterModel[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState<keyof EncounterModel>("created_at");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [requestIsProcessing, setRequestIsProcessing] = useState(false);
    const [selectedEncounterId, setSelectedEncounterId] = useState<string | null>(null);

    useEffect(() => {
        const loadEncounters = async () => {
            setRequestIsProcessing(true);

            try {
                const fetchedEncounters = await get("/encounters");
                console.info("Fetched encounters:", fetchedEncounters);
                setEncounters(fetchedEncounters);
            } catch (err) {
                console.warn("Error fetching encounters:", err);
                showMessage("Er is een fout opgetreden bij het ophalen van de geschiedenis.", "error");
            } finally {
                setRequestIsProcessing(false);
            }
        };

        loadEncounters();
    }, []);

    const handleBackToList = () => {
        setSelectedEncounterId(null);
    };

    const handleSort = (column: keyof EncounterModel) => {
        if (sortColumn === column) {
            setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortColumn(column);
            setSortOrder("desc");
        }
    };

    const handleViewEncounter = (encounterId: string) => {
        setSelectedEncounterId(encounterId);
    };

    const formatDate = (date: string): string => {
        const d = new Date(date);
        if (isNaN(d.getTime())) return date;
        let year = d.getFullYear().toString();
        let month = String(d.getMonth() + 1).padStart(2, "0");
        let day = String(d.getDate()).padStart(2, "0");
        let hours = String(d.getHours()).padStart(2, "0");
        let minutes = String(d.getMinutes()).padStart(2, "0");
        return `${day}-${month}-${year} ${hours}:${minutes}`;
    };

    const handleDelete = async (encounterId: string) => {
        if (!window.confirm("Weet je zeker dat je deze encounter wilt verwijderen?")) {
            return;
        }
        console.info("Deleting encounter with ID:", encounterId);
        setRequestIsProcessing(true)
        try {
            await del(`/encounters/${encounterId}`);
            setEncounters((prev) => prev.filter((encounter) => encounter.id !== encounterId));
        } catch (err) {
            console.warn("Error deleting encounter:", err);
            showMessage("Er is een fout opgetreden bij het verwijderen van het gesprek.", "error");
        }
        finally {
            setRequestIsProcessing(false);
        }
    };

    const sortedEncounters = [...encounters].sort((a, b) => {
        if (!sortColumn) return 0;

        const aValue = sortColumn === "created_at" ? new Date(a.created_at).getTime() : a[sortColumn] ?? '';
        const bValue = sortColumn === "created_at" ? new Date(b.created_at).getTime() : b[sortColumn] ?? '';

        return sortOrder === "asc"
            ? (aValue > bValue ? 1 : -1)
            : (aValue < bValue ? 1 : -1);
    });

    const filteredEncounters = sortedEncounters.filter((encounter) =>
        Object.values(encounter).some((value) => value?.toString().toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (selectedEncounterId) {
        return (
            <>
                <Button startIcon={<ArrowBackIcon/>} onClick={handleBackToList}>
                    Terug naar lijst
                </Button>
                <Encounter id={selectedEncounterId} sidebarOpen={sidebarOpen}/>
            </>
        );
    }

    return (
        <Container maxWidth="md">
            <TextField
                fullWidth
                placeholder="Zoeken..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon/>
                            </InputAdornment>
                        ),
                    }
                }}
                sx={{marginBottom: 2}}
            />

            {requestIsProcessing ? (
                <CircularProgress sx={{display: "block", margin: "20px auto"}}/>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 'bold'}}>
                                    <TableSortLabel
                                        active={sortColumn === "name"}
                                        direction={sortOrder}
                                        onClick={() => handleSort("name")}
                                    >
                                        Naam
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{fontWeight: 'bold'}}>
                                    <TableSortLabel
                                        active={sortColumn === "created_at"}
                                        direction={sortOrder}
                                        onClick={() => handleSort("created_at")}
                                    >
                                        Aangemaakt
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{fontWeight: 'bold'}}>
                                    Acties
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredEncounters.map((encounter) => (
                                <TableRow key={encounter.id}>
                                    <TableCell>{encounter.name}</TableCell>
                                    <TableCell>{formatDate(encounter.created_at)}</TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => handleViewEncounter(encounter.id)}>
                                            <VisibilityIcon/>
                                        </IconButton>
                                        <IconButton onClick={() => handleDelete(encounter.id)} color="error">
                                            <DeleteIcon/>
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Container>
    );
};

export default EncountersList;
